import { formatNumber } from "../../utils";
import {
  endOfDay,
  isFuture,
  nextSunday,
  previousSunday,
  startOfDay,
  startOfYesterday,
  sub,
} from "date-fns";
import { nextSaturday } from "date-fns/esm";
import {
  DateAccumulatorInterface,
  ProgressByStandardRequestInterface,
  ProgressRequestInterface,
  ReportRequestInterface,
  SchoolsWithIntegral,
  ScopeSelectInterface,
  SectionData,
  TeacherData,
  UsageDataInterface,
  UsageRequestInterface,
} from "./ReportTypes";
import { isEmpty } from "lodash";

export const ProgressSeriesLimit = 100;

export const HOUR = 3600;
export const DAY = 86400; // number of seconds in a day
export const WEEK = DAY * 7;
export const MONTH = DAY * 30;

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const progressFilterOptions = [
  { key: "all", val: "All assignments and tests" },
  { key: "tests", val: "Tests only" },
  { key: "pushed-assignments", val: "Pushed assignments only" },
];

export const getStartEndDate = (): {
  yesterday: Date;
  lastWeek: Date;
  thirtyDays: Date;
  yearToDate: Date;
  lastYearToDate: Date;
  lastYearEnd: Date;
  currentkey: string;
  lastKey: string;
  today: Date;
} => {
  const today = new Date();
  const yesterday = startOfYesterday();
  const lastWeek = sub(yesterday, { days: 6 });
  const thirtyDays = sub(yesterday, { days: 30 });

  let yearToDate = new Date();
  const augustOfCurrentYear = new Date(today.getFullYear(), 7, 1);
  yearToDate = augustOfCurrentYear;
  if (isFuture(augustOfCurrentYear)) {
    yearToDate = new Date(today.getFullYear() - 1, 7, 1);
  }
  const lastYearToDate = new Date(yearToDate.getFullYear() - 1, 7, 1);
  const lastYearEnd = new Date(yearToDate.getFullYear(), 6, 31, 23, 59, 59);
  const currentkey = `${yearToDate.getFullYear().toString(10).slice(2)}-${(
    yearToDate.getFullYear() + 1
  )
    .toString(10)
    .slice(2)}`;
  const lastKey = `${lastYearToDate.getFullYear().toString(10).slice(2)}-${(
    lastYearToDate.getFullYear() + 1
  )
    .toString(10)
    .slice(2)}`;
  return {
    yesterday: yesterday,
    today: today,
    lastWeek: lastWeek,
    thirtyDays: thirtyDays,
    yearToDate: yearToDate,
    lastYearToDate: lastYearToDate,
    lastYearEnd: lastYearEnd,
    currentkey: currentkey,
    lastKey: lastKey,
  };
};

export const getDateInterval = ({
  start,
  end,
}: {
  start: number;
  end: number;
}): string => {
  // determine what size time interval by which to accumulate data
  let interval = "";
  if (end - start < DAY) {
    interval = "hour";
  } else if (end - start < DAY * 14) {
    // less than 2 weeks
    interval = "day";
  }
  if (end - start >= DAY * 14) {
    // 2 weeks or more
    interval = "week";
  }
  if (end - start > DAY * 90) {
    // more than 3ish months
    interval = "month";
  }
  return interval;
};

export const getAggregateDateLabels = ({
  interval,
  acc,
}: {
  interval: string;
  // acc: DateAccumulatorInterface[];
  acc: Date[];
}) => {
  const labels: string[] = [];
  interval === "hour"
    ? acc.map((entry) =>
        labels.push(
          // "en-GB" for military time, "en-US" (doesn't quite fit in-line) for 12 hour with AM/PM
          `${entry.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
          })}`
        )
      )
    : interval === "day"
    ? acc.map((entry) =>
        labels.push(
          `${entry.getMonth() + 1}/${entry.getDate()}/${entry.getFullYear()}`
        )
      )
    : interval === "week"
    ? acc.map((entry) =>
        // week = Sunday -- Saturday
        labels.push(
          `${entry.getMonth() + 1}/${entry.getDate()} - ${
            nextSaturday(entry).getMonth() + 1 // getMonth() returns 0 indexed month -> add 1
          }/${nextSaturday(entry).getDate()}`
        )
      )
    : acc.map((entry) =>
        labels.push(`${MONTHS[entry.getMonth()]} ${entry.getFullYear()}`)
      );

  return labels;
};

export const accumulateUsageData = ({
  data,
  dateRange,
}: {
  data: UsageDataInterface[];
  dateRange: { start: number; end: number };
}): {
  acc: Array<DateAccumulatorInterface>;
  aggregateDateLabels: any;
  aggregateTimeAccrued: any;
  aggregateProblemsSolved: any;
} => {
  const interval = getDateInterval({
    start: dateRange.start,
    end: dateRange.end,
  });

  if (data.length < 1) {
    const acc: never[] = [];
    const aggregateDateLabels: never[] = [];
    const aggregateTimeAccrued: never[] = [];
    const aggregateProblemsSolved: never[] = [];
    return {
      acc,
      aggregateDateLabels,
      aggregateTimeAccrued,
      aggregateProblemsSolved,
    };
  }

  // construct interval buckets
  const dateIntervals = [new Date(dateRange.start * 1000)];
  if (interval === "hour") {
    // use start of the day so that labels go from 12:00AM - 11:00PM
    dateIntervals[0] = startOfDay(dateIntervals[0]);
  }
  if (interval === "hour") {
    let current = dateIntervals[0];
    for (let i = 0; i < 23; i++) {
      const next = new Date((current.getTime() / 1000 + HOUR) * 1000);
      dateIntervals.push(next);
      current = next;
    }
  } else if (interval === "day") {
    let current = new Date(dateRange.start * 1000);
    while (current.getTime() / 1000 < dateRange.end) {
      const nextDay = new Date(current);
      nextDay.setDate(current.getDate() + 1);
      dateIntervals.push(nextDay);
      current = nextDay;
    }
  } else if (interval === "week") {
    if (dateIntervals[0].getDay() !== 0) {
      dateIntervals[0] = previousSunday(dateIntervals[0]);
    }
    const startDay = new Date(dateRange.start * 1000);
    let current = startDay.getDay() !== 0 ? previousSunday(startDay) : startDay; //
    while (current.getTime() / 1000 < dateRange.end) {
      const nextWeek = nextSunday(current);
      nextWeek.getTime() / 1000 < dateRange.end && dateIntervals.push(nextWeek);
      current = nextWeek;
    }
  } else if (interval === "month") {
    let current = new Date(dateRange.start * 1000);
    while (current.getTime() / 1000 < dateRange.end) {
      const nextMonth = new Date(current);
      nextMonth.setMonth(current.getMonth() + 1);
      nextMonth.getTime() / 1000 < dateRange.end &&
        dateIntervals.push(nextMonth);
      current = nextMonth;
    }
  }

  /** create an array of objects (representing each time "bucket") to represent each date interval in a graph */
  const dateAccumulator = new Array<DateAccumulatorInterface>();
  dateIntervals.map((interval) =>
    dateAccumulator.push({
      date: interval,
      time: 0,
      problems: 0,
    })
  );

  const intervalSpan =
    interval === "hour"
      ? HOUR
      : interval === "day"
      ? DAY
      : interval === "week"
      ? WEEK
      : MONTH;

  let index = 0;
  data
    .sort((a, b) => a.time - b.time) // sort data (ascending) by timestamp
    .forEach((entry: UsageDataInterface) => {
      const entryTimeMs = entry.time * 1000;

      // Skip entries before the first bucket - should be enforced by the API, but just in case
      if (entryTimeMs < dateAccumulator[0].date.getTime()) {
        return;
      }

      // Skip entries after the last bucket's interval - should be enforced by the API, but just in case
      if (
        entryTimeMs >
        dateAccumulator[dateAccumulator.length - 1].date.getTime() +
          intervalSpan * 1000
      ) {
        return;
      }

      // Find the correct bucket
      while (
        index < dateAccumulator.length - 1 &&
        entryTimeMs >= dateAccumulator[index + 1].date.getTime()
      ) {
        index++;
      }

      dateAccumulator[index].time += entry.stats.t / 60;
      dateAccumulator[index].problems += entry.stats.n;
    });

  const aggregateTimeAccrued = dateAccumulator.map((entry) =>
    Math.round(entry.time)
  );
  const aggregateProblemsSolved = dateAccumulator.map(
    (entry) => entry.problems
  );
  const aggregateDateLabels = getAggregateDateLabels({
    interval: interval,
    acc: dateAccumulator.map((entry) => entry.date),
  });

  return {
    acc: dateAccumulator,
    aggregateDateLabels,
    aggregateTimeAccrued,
    aggregateProblemsSolved,
  };
};

export const formatBasicData = ({ data }: { data: { data: any } }) =>
  /**
   * @param data nested object returned from /usage endpoint, without series? query param
   * @returns a cleaned array of data that can be passed to accumulateUsageData()
   */
  Object.entries(data.data).map((entry: any) => ({
    time: parseInt(entry[0]),
    stats: {
      t: entry[1].t,
      n: entry[1].n,
    },
  }));

export const formattedLabelValue = (tooltipItem: any) => {
  return (
    tooltipItem.dataset.label + ": " + formatNumber(Math.floor(tooltipItem.raw))
  );
};

export const dataTabs = [
  {
    name: "Time Spent",
    slug: "time-spent",
  },
  {
    name: "Problems Solved",
    slug: "problems-solved",
  },
  {
    name: "Compare Time Spent",
    slug: "compare-time-spent",
  },
  {
    name: "Compare Problems Solved",
    slug: "compare-problems-solved",
  },
];

// Take Received IDs and Table Data, return an array of the object properties specified
export const setIdsArray = (
  selectedItems: any,
  tableData: any,
  objectProperties: string[]
) => {
  const selectedDataIds = Object.keys(selectedItems);
  const selectedIds = [];
  if (Array.isArray(objectProperties)) {
    for (let j = 0; j < selectedDataIds.length; j++) {
      const arrayId = parseInt(selectedDataIds[j]);
      // initialize an empty object to push into final array
      const element = JSON.parse("{}");
      for (let i = 0; i < objectProperties.length; i++) {
        if (tableData[arrayId]) {
          element[objectProperties[i].toString()] =
            tableData[arrayId][objectProperties[i]];
        }
      }
      // Ideally we don't have any empty objects in the first case, but as a fallback make sure they don;t
      // Make it into any requests
      if (!isEmpty(element)) {
        selectedIds.push(element);
      }
    }
  }
  return selectedIds;
};

/**
 * Returns the count of selected items and a string/number representing the selection ("All" if all are selected).
 * @param stateData UsageLocalStorageInterface
 * @param schoolsWithIntegral array or null
 * @param teacherData array or null
 * @param sectionData array or null
 */
export function getCountAndSelected(
  stateData: ScopeSelectInterface,
  schoolsWithIntegral: any[] | null | undefined,
  teacherData: any[] | null | undefined,
  sectionData: any[] | null | undefined
): { selectedCount: number; selectedInfo: string | number } {
  const { scope, selectedSchools, selectedTeachers, selectedSections } =
    stateData;

  const selectedCount =
    scope === "Sections"
      ? Object.keys(selectedSections || {}).length
      : scope === "Teachers"
      ? Object.keys(selectedTeachers || {}).length
      : Object.keys(selectedSchools || {}).length;

  let selectedInfo: string | number = selectedCount;
  if (
    scope === "Sections" &&
    sectionData &&
    selectedCount === sectionData.length
  ) {
    selectedInfo = "All";
  } else if (
    scope === "Teachers" &&
    teacherData &&
    selectedCount === teacherData.length
  ) {
    selectedInfo = "All";
  } else if (
    scope === "Schools" &&
    schoolsWithIntegral &&
    selectedCount === schoolsWithIntegral.length
  ) {
    selectedInfo = "All";
  }
  return { selectedCount, selectedInfo };
}

/**
 * This pattern, while I don't love it is used for /usage, /progress and /progressByStandard
 * So this function exists to abstract some of the messier parts of this pattern so we don't have to deal with it
 * elsewhere.
 * @param stateData The ScopeSelectInterface containing the scope and selected items
 * @param schoolsWithIntegral The array of schools with integral data
 * @param teacherData The array of teacher data
 * @param sectionData The array of section data
 */
export function buildRequestParams<
  T extends
    | UsageRequestInterface
    | ProgressRequestInterface
    | ProgressByStandardRequestInterface
>(
  stateData: ScopeSelectInterface | null,
  schoolsWithIntegral: SchoolsWithIntegral[],
  teacherData: TeacherData[],
  sectionData: SectionData[],
  options?: {
    /** Optional, limit number of items included in series, default to 5 */
    seriesLimit?: number;
  }
): T | null {
  if (!stateData) return null;

  // Handle options
  if (!options) options = {};
  if (!options.seriesLimit) options.seriesLimit = 5;

  let selectedIds: any[] = [];
  let key: keyof ReportRequestInterface | null = null;
  let dataSource: any[] = [];

  if (
    stateData.scope === "Schools" &&
    stateData.selectedSchools &&
    schoolsWithIntegral.length > 0
  ) {
    key = "schoolIds";
    dataSource = schoolsWithIntegral;
    selectedIds = setIdsArray(stateData.selectedSchools, dataSource, [
      "schoolid",
    ]).map((school) => school.schoolid);
  } else if (
    stateData.scope === "Teachers" &&
    stateData.selectedTeachers &&
    teacherData
  ) {
    key = "teacherIds";
    dataSource = teacherData;
    selectedIds = setIdsArray(stateData.selectedTeachers, dataSource, [
      "_id",
    ]).map((teacher) => teacher._id);
  } else if (
    stateData.scope === "Sections" &&
    stateData.selectedSections &&
    sectionData
  ) {
    key = "sectionIds";
    dataSource = sectionData;
    selectedIds = setIdsArray(stateData.selectedSections, dataSource, [
      "sectionId",
    ]).map((section) => section.sectionId);
  } else {
    return null;
  }

  const params = {} as T;
  if (stateData.startDate?.getTime()) {
    params.start = Math.floor(stateData.startDate.getTime() / 1000).toString();
  }
  if (stateData.endDate?.getTime()) {
    params.end = Math.floor(
      endOfDay(stateData.endDate).getTime() / 1000
    ).toString();
  }
  if (selectedIds.length > 0 && key) {
    params[key] = selectedIds;
  }
  if (selectedIds.length > 1 && selectedIds.length <= options.seriesLimit) {
    params.series = selectedIds;
  } else if (selectedIds.length > options.seriesLimit) {
    params.series = [];
  }
  return params;
}

/**
 * A validator for buildRequestParams. Does not check all the params, only that it meets minimum requirements.
 * For a successful report request, it must have at least one ID and a start and end date.
 * It's just a extra safety mechanism to prevent API calls in the event of some unexpected partial state.
 * @param params THe subset of parameters used to define a report scope
 * @param includeDistrict Optional, when true validation can pass if the district is selected
 */
export function validParams(
  params: UsageRequestInterface | null | undefined,
  includeDistrict = false
): boolean {
  if (!params) return false;
  const hasIds =
    !!params.teacherIds ||
    !!params.schoolIds ||
    !!params.sectionIds ||
    (includeDistrict && params.district === "true");
  const hasDates = !!params.start && !!params.end;
  return hasIds && hasDates;
}
